import * as angular from "angular";
import {IController, ITimeoutService} from "angular";
import * as _ from "lodash";
import {MapsCommonService} from "../../../map/services/mapsCommon.service";
import {ClusteringAreaModel} from "../../models/clustering-area.model";

export class PlanningEditClusteringAreaViewCtrl implements IController {
  clusteringArea: ClusteringAreaModel;
  onCancel: Function;
  onSave: Function;

  public area: ClusteringAreaModel;
  public map: any;
  public clusteringAreaPolygon: any;
  public polygonEvents: any;
  private mapInitialized: boolean;
  private googlePolygonRef: any;
  private pathListenerHandles: any[] = [];

  /* @ngInject */
  constructor(private mapsCommonService: MapsCommonService, private $timeout: ITimeoutService) {
  }

  $onDestroy() {
    this._clearPathListeners();
  }

  $onInit() {
    this.area = angular.copy(this.clusteringArea) || {};
    this._initMap();
    this._initPolygonEvents();
    this._setPolygon();
    this.$timeout(300).then(() => {
      this._centerMap();
    });
  }

  onColorChange() {
    this._setPolygon();
  }

  onCenterMap() {
    this._centerMap();
  }

  onReset() {
    this.area = angular.copy(this.clusteringArea) || {};
    this._setPolygon();
    this._centerMap();
  }

  onCancelClick() {
    this.onCancel();
  }

  onSaveClick(form) {
    form.$setSubmitted(true);
    if (this.googlePolygonRef) {
      this._syncCoordinatesFromPolygon(this.googlePolygonRef);
    }
    if (!form.$valid || !this._hasPolygon()) {
      return;
    }

    const edited: ClusteringAreaModel = {
      ...this.clusteringArea,
      name: this.area.name,
      color: this.area.color,
      coordinates: angular.copy(this._getCoordinatesForSave()),
      edited: true
    };

    this.onSave({clusteringArea: edited});
  }

  private _initMap() {
    this.map = {
      center: this.mapsCommonService.getSiteLocation(),
      zoom: 12,
      bounds: {},
      options: {
        styles: this.mapsCommonService.getDefaultStyle(),
        scaleControl: true,
        mapTypeControl: false
      },
      events: {
        idle: (map) => {
          if (!this.mapInitialized) {
            this.mapInitialized = true;
            google.maps.event.trigger(map, 'resize');
            this._centerMap();
          }
        }
      }
    };
  }

  private _initPolygonEvents() {
    // Native google.maps.Polygon editable mode — no Maps JavaScript Drawing Library / DrawingManager.
    this.polygonEvents = {
      click: (polygon) => {
        this._registerPolygon(polygon);
      },
      mouseup: (polygon) => {
        this._registerPolygon(polygon);
        this._syncCoordinatesFromPolygon(polygon);
      },
      dragend: (polygon) => {
        this._registerPolygon(polygon);
        this._syncCoordinatesFromPolygon(polygon);
      },
      rightclick: (polygon, eventName, model, event) => {
        this._registerPolygon(polygon);
        const vertex = _.get(event, '[0].vertex');

        if (!_.isNil(vertex)) {
          const path = polygon.getPath();
          if (path.getLength() > 3) {
            path.removeAt(vertex);
            this._syncCoordinatesFromPolygon(polygon);
          }
          return;
        }

        this._togglePolygonEditable();
      }
    };
  }

  private _togglePolygonEditable() {
    if (!this.clusteringAreaPolygon) {
      return;
    }

    const isEditable = !this.clusteringAreaPolygon.editable;
    if (!isEditable) {
      this._clearPathListeners();
    }

    const polygonCopy = angular.copy(this.clusteringAreaPolygon);
    polygonCopy.editable = isEditable;
    this.clusteringAreaPolygon = undefined;
    this.$timeout(100).then(() => {
      this.clusteringAreaPolygon = polygonCopy;
    });
  }

  private _registerPolygon(polygon) {
    if (!polygon || !_.isFunction(polygon.getPath)) {
      return;
    }

    if (this.googlePolygonRef === polygon && this.pathListenerHandles.length) {
      return;
    }

    this._clearPathListeners();
    this.googlePolygonRef = polygon;

    const path = polygon.getPath();
    if (!path) {
      return;
    }

    const sync = () => {
      this._syncCoordinatesFromPolygon(polygon);
    };

    this.pathListenerHandles = [
      google.maps.event.addListener(path, 'set_at', sync),
      google.maps.event.addListener(path, 'insert_at', sync),
      google.maps.event.addListener(path, 'remove_at', sync)
    ];
  }

  private _clearPathListeners() {
    _.forEach(this.pathListenerHandles, (handle) => {
      google.maps.event.removeListener(handle);
    });
    this.pathListenerHandles = [];
    this.googlePolygonRef = undefined;
  }

  private _setPolygon() {
    const areaForMap = this._getAreaForMap();
    if (!areaForMap.coordinates?.length) {
      this._clearPathListeners();
      this.clusteringAreaPolygon = undefined;
      return;
    }

    this._clearPathListeners();

    const polygons = this.mapsCommonService.buildClusteringAreaPolygons([areaForMap], true);
    const polygon: any = _.head(polygons);
    if (polygon) {
      polygon.editable = true;
      polygon.draggable = false;
      this.clusteringAreaPolygon = polygon;
    }
  }

  private _getAreaForMap(): ClusteringAreaModel {
    return {
      ...this.area,
      id: this.area.id || this.clusteringArea?.id,
      color: this.area.color || '#6060FB',
      name: this.area.name || this.area.serviceAreaName || _.get(this.area, 'areaReference.name')
    };
  }

  private _centerMap() {
    const coordinates = this._normalizeCoordinates(this.area.coordinates);
    if (!coordinates.length) {
      return;
    }

    this.map.bounds = this.mapsCommonService.getCoordinatesBounds(_.map(coordinates, (coordinate) => {
      return [coordinate.longitude, coordinate.latitude];
    }));
  }

  private _syncCoordinatesFromPolygon(polygon) {
    const path = polygon && _.isFunction(polygon.getPath) ? polygon.getPath() : undefined;
    if (!path) {
      return;
    }

    this.$timeout(0).then(() => {
      this.area.coordinates = this._coordinatesFromPath(path);
    });
  }

  private _coordinatesFromPath(path) {
    let coordinates = _.map(path.getArray(), (coords) => {
      return {
        latitude: coords.lat(),
        longitude: coords.lng(),
        lat: coords.lat(),
        lng: coords.lng()
      };
    });

    if (coordinates.length > 1) {
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      if (first.latitude === last.latitude && first.longitude === last.longitude) {
        coordinates = coordinates.slice(0, -1);
      }
    }

    return coordinates;
  }

  private _getCoordinatesForSave() {
    if (this.googlePolygonRef) {
      const path = this.googlePolygonRef.getPath();
      if (path) {
        return this._coordinatesFromPath(path);
      }
    }

    return this._normalizeCoordinates(this.area.coordinates);
  }

  private _normalizeCoordinates(coordinates) {
    return _.chain(coordinates)
      .map((coordinate) => {
        const latitude = coordinate?.latitude ?? coordinate?.lat;
        const longitude = coordinate?.longitude ?? coordinate?.lng;
        if (_.isNil(latitude) || _.isNil(longitude)) {
          return undefined;
        }
        return {latitude, longitude};
      })
      .compact()
      .value();
  }

  private _hasPolygon(): boolean {
    return !!this._normalizeCoordinates(this.area.coordinates).length;
  }

}

angular
  .module('bringoz.admin.planning')
  .component('planningEditClusteringAreaView', {
    bindings: {
      clusteringArea: '<',
      onCancel: '&',
      onSave: '&'
    },
    template: require('./planning-edit-clustering-area-view.html').default,
    controller: PlanningEditClusteringAreaViewCtrl
  });
